import type { Flow, FlowNode, VisualStyle } from "@learnengine/shared-types";

/**
 * A self-contained sample Flow for testing locally with NO backend and NO AI.
 *
 * It deliberately spans visually distinct templates (atom, cell, pipeline,
 * graph, orbit) so you can watch a single style re-skin very different visuals —
 * which is exactly what the v2 style engine has to handle. Load it via the
 * "Preview offline demo" button on the landing screen.
 */
function demoNode(
  id: string,
  template: string,
  props: Record<string, unknown>,
  narrationText: string,
): FlowNode {
  return {
    id,
    template,
    durationSeconds: 8,
    audio: { id: `audio_${id}` },
    actions: [
      { type: "draw", at: 0 },
      { type: "highlight", at: 1.5 },
      { type: "fade", at: 7 },
    ],
    props,
    narrationText,
  };
}

export function buildDemoFlow(visualStyle: VisualStyle): Flow {
  return {
    flowId: "demo_local",
    version: "1.0",
    metadata: {
      title: "Style demo — atoms, cells & motion",
      topic: "Style demo",
      creatorId: "anonymous",
      complexityLevel: "beginner",
      language: "en",
      estimatedDurationSeconds: 40,
      visualStyle,
    },
    runtime: { tempo: "normal", allowSeek: true, allowPause: true, guideEnabled: false },
    nodes: [
      demoNode("sk0", "skin_demo_scene", { label: "v3 style-skin engine: same scene, clean vs hand-drawn (Rough.js)" },
        "Here's the new style engine. The very same drawing, on the left in clean studio style, and on the right hand-drawn with sketchy lines. One day soon, one click will switch your whole video between these styles."),
      demoNode("sk1", "skin_landscape_scene", { label: "Pick a Skin up top — watch the WHOLE scene change!" },
        "And here it is for real. Use the Skin menu at the top right to switch between Studio, Hand-drawn, Flat, and Doodle. The whole scene re-draws itself in the style you pick — instantly, and free."),
      demoNode("sk2", "actor_gallery_scene", { label: "The whole actor library — re-skins together!" },
        "And it's not just one scene. Here's the whole library of characters and props we rebuilt — animals, objects, people, nature. Switch the Skin and every single one redraws in that style at once."),
      demoNode("sk3", "skin_park_scene", { label: "A real scene, built from the library" },
        "Now watch a real scene come together from those pieces — a sunny day at the park, with a child, a happy dog, butterflies, and trees swaying. Every part of it follows whichever Skin you choose."),
      demoNode("sk4", "skin_counting_scene", { label: "And a real lesson: counting!" },
        "And here's a real little lesson. Let's count the animals together — one, two, three, four, five! The numbers and the animals all draw themselves in your chosen style."),
      demoNode("sk5", "skin_water_cycle", { label: "Water cycle — now skin-aware" },
        "The sun warms the sea and tiny drops float up to make clouds. When the clouds get heavy it rains, and the water runs back to the sea. Switch the Skin and the whole water cycle redraws."),
      demoNode("sk6", "skin_gravity", { label: "Gravity — now skin-aware" },
        "Gravity is an invisible pull that brings things down. Watch the apple fall from the tree, faster and faster, until it lands — in whichever style you pick!"),
      demoNode("x0", "xiaohei_idea_scene", { label: "A whole new illustration style!" },
        "Here's something new — a hand-drawn illustration style, where a little character named Xiaohei acts out the idea on paper. Each illustration style is its own type of video, not just a filter."),
      demoNode("j0", "journey_scene", { label: "Reusable actors: the butterfly leads the way!" },
        "Watch closely — the same little butterfly from our life-cycle lesson is now leading a child down the road. Every character is a building block we can reuse in any story!"),
      demoNode("n0", "car_speed_scene", { label: "More road each tick = faster!" },
        "Speed is just how much road you cover in a little bit of time. Watch the car zoom from start to finish while the clock ticks!"),
      demoNode("n1", "gravity_scene", { label: "What goes up must come down!" },
        "Gravity is like an invisible hand that always pulls things down. Watch the apple fall — it gets faster and faster until it lands!"),
      demoNode("n2", "water_cycle_scene", { label: "Water goes round and round!" },
        "The sun warms the sea and tiny water drops float up to make clouds. When the clouds get heavy, it rains, and the water runs back to the sea!"),
      demoNode("n3", "photosynthesis_scene", { label: "Plants make food from sunlight!" },
        "Plants are like little chefs. They drink water from their roots, catch sunlight with their leaves, and make their own food — breathing out fresh air for us!"),
      demoNode("n4", "fractions_scene", { label: "A fraction is a piece of the whole!" },
        "A fraction is just a piece of something whole. Cut a pizza in two and each piece is one half. Cut it into four and each piece is one quarter!"),
      demoNode("n5", "counting_scene", { label: "Let's count together!" },
        "Counting means adding one more each time. One apple, two apples, three — every time another drops in the basket, the number grows!"),
      demoNode("n6", "atom_orbital", { label: "Tiny worlds: the atom" },
        "Everything is made of atoms — a center with tiny electrons zipping around it, like planets racing around a sun!"),
    ],
  };
}
